import React from "react";
import { useSelector } from "react-redux";

//Utils and Constants
import {
  formatText,
  toLocaleString,
} from "../../../common-utils/string-utils/StringUtils";
import { constructSingleDateString } from "../../../common-utils/date-utils/DateUtils";
import {
  DATE_FORMATS,
  ExcelReportStatus,
  UrlKeys,
} from "../../../constants/GeneralConstants";
import { getOtsTotal } from "../../../utils/mavinMetrics";
import { useUrlKeyCheck } from "../../../mavin/utils/hooks/HooksUtil";
import { calculatePercentage } from "../../../common-utils/number-utils/NumberUtils";
import { checkIsCoralMediaSpecific } from "../../../common-utils/user-utils/userUtils";
import { checkIsCustomTgPresent } from "../../../utils/CampaignCustomTgUtils";

// Components
import { getLtsRatingAvg } from "../../../components/campaign-table-row/CampaignRowCells";
import { ProgressBar } from "../../../components/progress-bar/ProgressBar";
import AlertMessage from "../../../mavin/components/alert-message/AlertMessage";

// Section Components
function ImpressionsCardContent({
  tgOtsTotal,
  tgPercentage,
  otsTotal,
  targetGroupId = "",
  isCustomTgSelected = false,
}) {
  // final tg select check
  const isAnyTgSelected = targetGroupId || isCustomTgSelected;

  // check custom tg selected or not
  const tgImpString = isAnyTgSelected
    ? formatText(toLocaleString(tgOtsTotal))
    : "NA";

  return (
    <>
      <p className="mb-2 mt-0 d-flex align-items-center">
        <span className="d-inline-block border border-dark mr-2 square bg-light-green"></span>
        TG : {tgImpString}
        {/* {`(${tgPercentage}%)`}  */}
      </p>
      <p className="mb-2 mt-0 d-flex align-items-center">
        <span className="d-inline-block border border-dark mr-2 square bg-light-blue"></span>
        Total : {formatText(toLocaleString(otsTotal))}
      </p>

      {parseInt(otsTotal) > 0 && (
        <>
          <ProgressBar className="bg-light-green" percentage={tgPercentage} />
          <ProgressBar className="bg-light-blue" percentage={100} />
        </>
      )}
    </>
  );
}

function ExcelReportStatusAlert({ excelReportStatus = "" }) {
  // Selector
  const excelReportGenerateLoading = useSelector(
    (state) => state.campaignReport.generateCampaignExcelReportLoading
  );

  // check for failed status
  const isExcelReportFailed = excelReportStatus === ExcelReportStatus.FAILED;

  // alert box class
  const alertBoxClassName = "mb-0 py-2 pr-4";

  // excel report generate loading
  if (excelReportGenerateLoading) {
    return (
      <AlertMessage
        className={alertBoxClassName}
        spinnerClassName="spinner-border-sm"
        textMessage={
          <>
            Excel report data generating.
            <br />
            It can be downloaded once generation is done.
          </>
        }
      />
    );
  }

  // excel report failed
  if (isExcelReportFailed) {
    return (
      <AlertMessage
        className={`alert-danger ${alertBoxClassName}`}
        isLoading={false}
        textMessage="Excel report data generation is failed."
      />
    );
  }

  // return null
  return null;
}

function CampaignPerformance({
  campaign,
  summary,
  showExcelReportAlert = false,
}) {
  const {
    startTimestamp,
    campaignType = "",
    excelReportStatus = "",
  } = campaign || {};
  const { duration: campaignDuration } = summary || {};

  const startDate = constructSingleDateString(
    startTimestamp,
    DATE_FORMATS.full_month_with_date_year
  );

  // coral media
  const isCoralMediaSpecific = checkIsCoralMediaSpecific();

  return (
    <div className="mb-4 d-flex align-items-center justify-content-between">
      <div>
        <div className="d-flex align-items-center pb-2">
          <h4 className="sec-title mb-0">Campaign Performance</h4>

          {/* Coral Media : Campaign Type */}
          {isCoralMediaSpecific && campaignType && (
            <span className="ml-2 font-italic">
              (Campaign Type: {campaignType})
            </span>
          )}
        </div>

        <div>
          <p className="mb-0">
            <em className="text-small">
              Estimated for a {campaignDuration} days duration starting{" "}
              <strong>{startDate}</strong>
            </em>
          </p>
          <p className="mt-0 mb-2">
            <em className="text-small">
              For accurate performance details please enter campaign start date
              and duration at a city level or site level.
            </em>
          </p>
        </div>
      </div>

      {/* Excel Report Alert */}
      {showExcelReportAlert && (
        <ExcelReportStatusAlert excelReportStatus={excelReportStatus} />
      )}
    </div>
  );
}

function MavinStats({ statDisplayTitle, statDisplayValue }) {
  return (
    <div className="col">
      <div className="card h-100">
        <div className="card-body">
          <h4 className="card-title">{statDisplayTitle}</h4>
          <div className="card-text">{statDisplayValue}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Page Section
 */
function OverviewSection({
  campaign = {},
  summary = {},
  showExcelReportAlert = false,
}) {
  const { budget = 0, spend = 0, targetGroupId } = campaign || {};
  const { ltsRatingAvg, mediaCount } = summary || {};

  // custom tg
  const isCustomTgSelected = checkIsCustomTgPresent(campaign);

  const ltsAvg = getLtsRatingAvg(ltsRatingAvg);
  const { otsTotal, tgOtsTotal } = getOtsTotal(summary) || {};
  const tgPercentage = calculatePercentage(
    Number(tgOtsTotal),
    Number(otsTotal)
  );

  const mavinStats = {
    "No. of Sites": formatText(mediaCount),
    Impressions: (
      <ImpressionsCardContent
        tgOtsTotal={tgOtsTotal}
        otsTotal={otsTotal}
        tgPercentage={tgPercentage}
        targetGroupId={targetGroupId}
        isCustomTgSelected={isCustomTgSelected}
      />
    ),
    "Avg LTS rating": formatText(ltsAvg),
  };

  const { mantaraySpecific } = UrlKeys;
  const isMantaraySpecific = useUrlKeyCheck(mantaraySpecific);
  const totalBudget = budget ? toLocaleString(budget) : 0;
  const usedBudget = spend ? toLocaleString(Math.floor(spend)) : 0;
  const remainingBudget = budget - spend;
  const remainingBudgetFormatted = remainingBudget
    ? toLocaleString(Math.ceil(remainingBudget))
    : 0;

  // remaining budget className
  const remainingBudgetClassName =
    budget < spend ? "text-danger" : "text-success";

  isMantaraySpecific &&
    Object.assign(mavinStats, {
      Budget: (
        <>
          <p className="mb-1">Total : {totalBudget}</p>
          <p className="mb-1">Used : {usedBudget}</p>
          <p className="mb-0">
            Remaining :{" "}
            <span className={remainingBudgetClassName}>
              {remainingBudgetFormatted}
            </span>
          </p>
        </>
      ),
    });

  return (
    <>
      {/* Campaign Performance */}
      <CampaignPerformance
        campaign={campaign}
        summary={summary}
        showExcelReportAlert={showExcelReportAlert}
      />

      {/*Stats Card */}
      <div className="row">
        {Object.keys(mavinStats).map((eachKey, index) => (
          <MavinStats
            statDisplayTitle={eachKey}
            statDisplayValue={mavinStats[eachKey]}
            key={index}
          />
        ))}
      </div>
    </>
  );
}

export default OverviewSection;
